export const MODES = [
  { value: 'AUTOMATIC', label: 'Automatic', help: 'The charge is calculated from this rule. Nobody can change it on a bill.' },
  { value: 'MANUAL', label: 'Manual', help: 'An authorised user enters the amount on each bill (up to the maximum below).' },
  { value: 'AUTO_OVERRIDE', label: 'Automatic + Override', help: 'Calculated automatically; only an authorised user can override it, with a reason.' },
  { value: 'NO_CHARGE', label: 'No collection charge', help: 'Zero charge for this centre and collection type.' },
];

export const COLLECTION_TYPES = [
  { value: 'HOME', label: 'Home collection' },
  { value: 'WALKIN', label: 'Walk-in / centre visit' },
  { value: 'CAMP', label: 'Camp / corporate' },
  { value: 'OTHER', label: 'Other' },
];

export const BASES = [
  { value: 'ORDER', label: 'Once per order (bill)' },
  { value: 'VISIT', label: 'Once per patient per day (visit)' },
];

export const labelOf = (list, value) => list.find((x) => x.value === value)?.label || value || '-';

const MONEY_RE = /^\d+(\.\d{1,2})?$/;
export const isMoney = (v, max = 100000) => {
  const s = String(v ?? '').trim();
  return MONEY_RE.test(s) && Number(s) <= max;
};

export const emptyRuleForm = (centreId = '') => ({
  centreId: centreId === '' || centreId == null ? '' : String(centreId),
  collectionType: 'HOME',
  mode: 'AUTOMATIC',
  chargeBasis: 'ORDER',
  defaultAmount: '0',
  pinCode: '',
  areaName: '',
  freeAboveOrderValue: '',
  maxManualAmount: '0',
  allowWaiver: false,
  effectiveFrom: new Date().toISOString().slice(0, 10),
  effectiveTo: '',
  remarks: '',
  rowVersion: 0,
});

export const ruleToForm = (r) => ({
  centreId: String(r.centreId),
  collectionType: r.collectionType,
  mode: r.mode,
  chargeBasis: r.chargeBasis,
  defaultAmount: String(r.defaultAmount ?? 0),
  pinCode: r.pinCode || '',
  areaName: r.areaName || '',
  freeAboveOrderValue: r.freeAboveOrderValue == null ? '' : String(r.freeAboveOrderValue),
  maxManualAmount: String(r.maxManualAmount ?? 0),
  allowWaiver: !!r.allowWaiver,
  effectiveFrom: (r.effectiveFrom || '').slice(0, 10),
  effectiveTo: r.effectiveTo ? r.effectiveTo.slice(0, 10) : '',
  remarks: r.remarks || '',
  rowVersion: r.rowVersion,
});

/** Mirrors the server rules for fast feedback. The server remains the authority. Returns a list of messages. */
export const validateRuleForm = (f) => {
  const errors = [];
  if (!f.centreId) errors.push('Select a centre.');
  if (!MODES.some((m) => m.value === f.mode)) errors.push('Select a charge mode.');
  if (!COLLECTION_TYPES.some((t) => t.value === f.collectionType)) errors.push('Select a collection type.');
  if (!isMoney(f.defaultAmount)) errors.push('Default amount must be 0 to 100000 with at most 2 decimals.');
  if (!isMoney(f.maxManualAmount)) errors.push('Maximum manual amount must be 0 to 100000 with at most 2 decimals.');
  if (f.freeAboveOrderValue !== '' && !isMoney(f.freeAboveOrderValue, 10000000)) errors.push('Free-above order value must be a valid amount.');
  if (f.mode === 'NO_CHARGE' && Number(f.defaultAmount) !== 0) errors.push('A No Charge rule must have a default amount of 0.');
  if ((f.mode === 'MANUAL' || f.mode === 'AUTO_OVERRIDE') && !(Number(f.maxManualAmount) > 0)) {
    errors.push('Maximum manual amount must be greater than 0 for Manual and Automatic + Override modes.');
  }
  if (f.mode === 'AUTOMATIC' && Number(f.maxManualAmount) !== 0) errors.push('Maximum manual amount only applies to Manual and Automatic + Override modes.');
  if (f.pinCode && !/^[1-9][0-9]{5}$/.test(f.pinCode)) errors.push('Pincode must be a 6 digit number.');
  if (!f.effectiveFrom) errors.push('Effective-from date is required.');
  if (f.effectiveTo && f.effectiveFrom && f.effectiveTo < f.effectiveFrom) errors.push('Effective-to cannot be before effective-from.');
  if ((f.remarks || '').length > 200) errors.push('Remarks cannot exceed 200 characters.');
  if ((f.areaName || '').length > 100) errors.push('Area name cannot exceed 100 characters.');
  return errors;
};

export const toRulePayload = (f) => ({
  centreId: Number(f.centreId),
  collectionType: f.collectionType,
  mode: f.mode,
  chargeBasis: f.chargeBasis,
  defaultAmount: Number(f.defaultAmount),
  pinCode: f.pinCode?.trim() || null,
  areaName: f.areaName?.trim() || null,
  freeAboveOrderValue: f.freeAboveOrderValue === '' ? null : Number(f.freeAboveOrderValue),
  maxManualAmount: Number(f.maxManualAmount),
  allowWaiver: !!f.allowWaiver,
  effectiveFrom: f.effectiveFrom,
  effectiveTo: f.effectiveTo || null,
  remarks: f.remarks?.trim() || null,
  rowVersion: Number(f.rowVersion) || 0,
});

export const formatMoney = (v) => `₹ ${(Number(v) || 0).toFixed(2)}`;

/** One readable message from an API error (never the raw payload). */
export const describeApiError = (err, fallback = 'The request failed.') => {
  const data = err?.response?.data;
  const details = Array.isArray(data?.errors) ? data.errors.filter((e) => e && e !== data.message) : [];
  return [data?.message || fallback, ...details].join(' ');
};
